/**
 * ChatSidebar Component
 * WhatsApp-style session list with avatars, search, and status indicators
 */
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useChat } from "../../hooks/useChat";
import { ChatSession } from "../../types/chat.types";
import {
  Search,
  Plus,
  MessageSquare,
  Trash2,
  BarChart3,
  HelpCircle,
  Bot,
} from "lucide-react";
import { Button } from "../common";

interface ChatSidebarProps {
  onSelectSession?: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ onSelectSession }) => {
  const { sessions, currentSession, selectSession, fetchSessions, deleteSession, startNewChat } = useChat();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Delete "${session.title || "New Conversation"}"? This cannot be undone.`
    );
    if (confirmed) {
      await deleteSession(session.id);
      await fetchSessions();
    }
  };

  const handleSelectSession = (session: ChatSession) => {
    selectSession(session);
    onSelectSession?.();
  };

  const filteredSessions = sessions.filter((session: ChatSession) =>
    (session.title || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-slate-900 border-r border-slate-800/50">
      {/* Header */}
      <div className="p-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Conversations</h2>
              <p className="text-xs text-slate-400">
                {sessions.length} {sessions.length === 1 ? "chat" : "chats"}
              </p>
            </div>
          </div>
          <Button
            variant="gradient"
            size="sm"
            onClick={startNewChat}
            icon={<Plus className="w-4 h-4" />}
          >
            New
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
          />
        </div>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto">
        {filteredSessions.length === 0 ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-700/50 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-slate-500" />
            </div>
            <p className="text-slate-400 font-medium">No conversations yet</p>
            <p className="text-sm text-slate-500 mt-1">Start a new chat to begin</p>
          </div>
        ) : (
          filteredSessions.map((session: ChatSession) => (
            <SessionCard
              key={session.id}
              session={session}
              isActive={currentSession?.id === session.id}
              onClick={() => handleSelectSession(session)}
              onDelete={(e) => handleDelete(session, e)}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface SessionCardProps {
  session: ChatSession;
  isActive: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  isActive,
  onClick,
  onDelete,
}) => {
  const isClosed = !session.is_active;

  // Get icon based on session type
  const getSessionIcon = () => {
    const title = session.title?.toLowerCase() || "";
    if (title.includes("create") || title.includes("job")) {
      return <BarChart3 className="w-4 h-4" />;
    } else if (title.includes("query") || title.includes("search")) {
      return <Search className="w-4 h-4" />;
    } else if (title.includes("help")) {
      return <HelpCircle className="w-4 h-4" />;
    }
    return <MessageSquare className="w-4 h-4" />;
  };

  // Generate color based on session ID for avatar
  const getAvatarColor = () => {
    const colors = [
      "from-primary-500 to-accent-500",
      "from-emerald-500 to-teal-500",
      "from-orange-500 to-red-500",
      "from-blue-500 to-indigo-500",
      "from-pink-500 to-rose-500",
    ];
    const index = session.id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div
      onClick={onClick}
      className={`p-4 border-b border-slate-700/30 cursor-pointer transition-all relative group ${
        isActive
          ? "bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-l-2 border-l-primary-500"
          : "hover:bg-slate-800/50"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`relative flex-shrink-0`}>
          <div
            className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor()} flex items-center justify-center text-white`}
          >
            {getSessionIcon()}
          </div>
          {/* Online/Closed Status */}
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-800 ${
              isClosed ? "bg-slate-500" : "bg-emerald-500"
            }`}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-white font-medium truncate text-sm">
              {session.title || "New Conversation"}
            </h3>
            <span className="text-xs text-slate-500 flex-shrink-0">
              {format(new Date(session.last_message_at + "Z"), "MMM d")}
            </span>
          </div>

          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-400">
              {session.message_count || 0} messages
            </span>
            {isClosed && (
              <span className="text-xs bg-slate-700/50 px-2 py-0.5 rounded text-slate-400">
                Closed
              </span>
            )}
          </div>
        </div>

        {/* Delete Button */}
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-500/20 text-slate-400 hover:text-red-400"
          title="Delete conversation"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
